<script setup lang="ts">
//#region types
export interface UiDetailsTreeRow {
  cols: Array<string | number | null>;
}

export interface UiDetailsTreeSection {
  title?: string;
  header?: Array<string | number>;
  rows: UiDetailsTreeRow[];
  visible?: boolean;
  columnsWeight?: number[];
}

export interface UiDetailsTree {
  rootTitle?: string;
  rootRows?: UiDetailsTreeRow[];
  sections?: UiDetailsTreeSection[];
  rootColumnsWeight?: number[];
}

export interface Props {
  blocks?: UiDetailsTree[];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  blocks: () => [],
});
//#endregion

//#region methods
function getGridTemplate(columnsWeight: number[] | undefined, fallback: number[]) {
  const columns = columnsWeight?.length ? columnsWeight : fallback;
  const template = columns
    .map((value) => `minmax(0, ${value}fr)`)
    .join(' ');

  return {
    gridTemplateColumns: template,
  };
}
//#endregion
</script>

<template>
  <div class="ui-details-tree">
    <div
      v-for="(block, index) in props.blocks"
      :key="index"
      class="ui-details-tree__block"
    >
      <div
        v-if="block.rootTitle && block.rootRows && block.rootRows.length"
        class="ui-details-tree__title"
      >
        {{ block.rootTitle }}
      </div>
      <div
        v-if="block.rootTitle && block.rootRows && block.rootRows.length"
        class="ui-details-tree__section"
      >
        <div class="ui-details-tree__table">
          <div
            v-for="(row, rowIndex) in block.rootRows"
            :key="rowIndex"
            class="ui-details-tree__row"
            :style="getGridTemplate(block.rootColumnsWeight, [1, 1])"
          >
            <span
              v-for="(column, i) in row.cols"
              :key="i"
              :class="[
                'ui-details-tree__cell',
                { 'ui-details-tree__cell_right': i > 0 },
              ]"
            >
              {{ column }}
            </span>
          </div>
        </div>
      </div>

      <template
        v-for="section in block.sections || []"
        :key="section.title"
      >
        <div
          v-if="section.visible !== false"
          class="ui-details-tree__section"
        >
          <div
            v-if="section?.header?.length"
            class="ui-details-tree__title ui-details-tree__title_grid"
            :style="getGridTemplate(section.columnsWeight, [2, 1.5, 1.5])"
          >
            <span
              v-for="(column, i) in section.header"
              :key="i"
              :class="[
                'ui-details-tree__cell',
                { 'ui-details-tree__cell_right': i > 0 },
              ]"
            >
              {{ column }}
            </span>
          </div>
          <div class="ui-details-tree__table ui-details-tree__table_grid">
            <div
              v-for="(rowItem, rowIndex) in section.rows"
              :key="rowIndex"
              class="ui-details-tree__row"
              :style="getGridTemplate(section.columnsWeight, [2, 1.5, 1.5])"
            >
              <span
                v-for="(column, i) in rowItem.cols"
                :key="i"
                :class="[
                  'ui-details-tree__cell',
                  { 'ui-details-tree__cell_right': i > 0 },
                ]"
              >
                {{ column }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.ui-details-tree {
  $root: &;

  &__title {
    background-color: var(--background-surface);
    border-radius: 5px;
    padding: 9px 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
  }

  &__row {
    padding: 8px 4px 8px 0;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--border-divider);

    &:last-child {
      border-bottom: 0;
    }
  }


  &__title_grid,
  &__table_grid #{$root}__row {
    display: grid;
    column-gap: 12px;
    align-items: center;
    width: 100%;
  }

  &__cell {
    font-size: 14px;
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;

    &_right {
      text-align: right;
    }
  }

  &__section {
    position: relative;
    padding-left: 18px;

    #{$root}__table {
      margin-left: 14px;
      padding-left: 0;
    }

    #{$root}__title {
      margin-left: 4px;
      position: relative;
      overflow: initial;

      &::before {
        content: '';
        position: absolute;
        left: -10px;
        top: 50%;
        height: 1px;
        width: 10px;
        background-color: var(--border-divider);
      }
    }

    &:not(:last-child) {
      #{$root}__table {
        &::after {
          content: '';
          position: absolute;
          left: 12px;
          top: 0;
          height: 100%;
          width: 1px;
          background-color: var(--border-divider);
        }
      }

      #{$root}__title::after {
        content: '';
        position: absolute;
        left: -10px;
        top: 0;
        height: 100%;
        width: 1px;
        background-color: var(--border-divider);
      }
    }

    &:last-child {
      #{$root}__title::after {
        content: '';
        position: absolute;
        left: -10px;
        top: 0;
        height: 50%;
        width: 1px;
        background-color: var(--border-divider);
      }
    }
  }
}
</style>

